import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { PROVIDER_SECTION_BREADCRUMB } from './provider-breadcrumb'
import { IconNotification } from '../../../../../shared/icon/icon'
import { FleetPage } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnText } from '../../../../../shared/table-builder/table-columns'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { authGet } from '../../../../../shared/sso-workspace/auth-api'
import { formatAgo, formatDate, formatDateTime, serverMessage } from '../../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

export const PAGE_SIZE = 100

// Every SSO email the platform keeps a record of (notification_ledgers), in
// words. The tone says how urgent it was for whoever got it.
export const ALERT_KINDS = {
  credential_expiry: { label: 'Credential expiring', tone: 'warn' },
  credential_rejected: { label: 'Credential rejected', tone: 'bad' },
  domain_at_risk: { label: 'Domain at risk', tone: 'warn' },
  domain_deactivated: { label: 'Domain deactivated', tone: 'bad' },
  domain_restored: { label: 'Domain restored', tone: 'ok' },
  notify_members: { label: 'Password link to members', tone: null },
  consent_approved: { label: 'Consent approved', tone: 'ok' },
  consent_expired: { label: 'Consent request expired', tone: null }
}

const kindOf = kind => ALERT_KINDS[kind] || { label: kind, tone: null }

// What the email was about, as a sentence fragment, from its threshold key
// (for credential expiry "<days>:<RFC 3339 expiry>"; for a rejected
// credential "<status>:<certificate thumbprint or credential id>").
const REJECTED_DETAIL = {
  not_registered: "the certificate isn't registered on the app",
  secret_invalid: "the client secret isn't valid",
  secret_expired: 'the client secret has expired',
  app_not_found: "the app isn't set up in the directory",
  client_id_wrong: 'the client ID is wrong',
  rejected: 'the identity provider rejected it',
  not_configured: 'no credential is set up'
}

export const alertDetail = row => {
  if (row.kind === 'credential_rejected') {
    const key = String(row.thresholdKey || '')
    const i = key.indexOf(':')
    const status = i > 0 ? key.slice(0, i) : key
    return REJECTED_DETAIL[status] ? `Not accepted: ${REJECTED_DETAIL[status]}` : 'Not accepted by the identity provider'
  }
  if (row.kind === 'credential_expiry') {
    const key = String(row.thresholdKey || '')
    const i = key.indexOf(':')
    const days = key.slice(0, i)
    const date = key.slice(i + 1)
    if (i > 0 && date && !Number.isNaN(new Date(date).getTime())) return `${days} days before it expires on ${formatDate(date)}`
  }
  return ''
}

const subjectLink = row => {
  // The shared app's own credential, checked before any connection used it.
  if (row.kind === 'credential_rejected' && !row.subjectID) return <Link to="/admin/sso/health">Shared sign-in app</Link>
  if (!row.subjectLabel) return <span className="sw-hint">Removed</span>
  if (row.subjectType === 'organization_domain') return <Link to={`/admin/sso/domains?domain=${encodeURIComponent(row.subjectLabel)}`}>{row.subjectLabel}</Link>
  if (row.providerID) return <Link to={`/admin/sso/connections/${row.providerID}`}>{row.subjectLabel}</Link>
  return row.subjectLabel
}

// Alerts sent (audit F9, section 4): every single sign-on email, not only
// credential expiry -- domain at risk, deactivated and restored, the
// password link to a deactivated domain's members, and the consent emails
// -- so "were they warned?" has an answer. What it was about links to its
// connection; filter by kind; older pages load with a cursor.
//
// The list is the table-builder's AG Grid. The kind filter and the paging
// run on the server: the grid is keyed on the kind, so a new kind remounts
// it and its onReady loads the first page; "Load older alerts" appends the
// next page (cursor: the last id) to the rows the grid already holds.
export const CredentialNotificationList = () => {
  const [kind, setKind] = useState('')
  const [rows, setRows] = useState(undefined)
  const [more, setMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, failure, setError] = useErrorLine()

  const tableRef = useRef()

  // Every load takes a sequence number; an answer that is no longer current
  // (the kind changed while it was in flight) is dropped.
  const seq = useRef(0)

  const load = async () => {
    const mine = ++seq.current
    setError(null)
    try {
      const page = (await authGet('credential-notification/all', { limit: PAGE_SIZE, kind })) || []
      if (mine !== seq.current) return
      setRows(page)
      setMore(page.length === PAGE_SIZE)
    } catch (err) {
      if (mine !== seq.current) return
      setRows(null)
      setError(serverMessage(err, "Sent alerts couldn't be loaded."), err)
    }
  }

  const loadOlder = async () => {
    if (!rows || !rows.length) return
    const mine = seq.current
    setLoadingMore(true)
    try {
      const page = (await authGet('credential-notification/all', { limit: PAGE_SIZE, kind, before: rows[rows.length - 1].id })) || []
      if (mine !== seq.current) return
      setRows(r => (Array.isArray(r) ? [...r, ...page] : r))
      setMore(page.length === PAGE_SIZE)
    } catch (err) {
      if (mine !== seq.current) return
      setError(serverMessage(err, "Older alerts couldn't be loaded."), err)
    } finally {
      if (mine === seq.current) setLoadingMore(false)
    }
  }

  // Display values flattened onto the row, so the grid's quick filter, sort
  // and CSV export work off the words shown rather than raw enums.
  const gridRows = useMemo(
    () =>
      Array.isArray(rows)
        ? rows.map(r => ({ ...r, alert: kindOf(r.kind).label, about: r.subjectLabel || '', organization: r.organizationName || '' }))
        : rows,
    [rows]
  )
  const gridList = useGridList(gridRows)

  return (
    <BreadcrumbView parent={PROVIDER_SECTION_BREADCRUMB} Icon={IconNotification} text="Alerts sent">
      <FleetPage
        tabs
        title="Alerts sent"
        purpose="Every single sign-on email the platform sent: expiring credentials, domains at risk, deactivated or restored, and consent requests. Use it to answer whether someone was warned.">
        <div className="sw-toolbar">
          <select className="form-select form-select-sm" aria-label="Alert" value={kind} onChange={e => { setRows(undefined); setMore(false); setKind(e.target.value) }}>
            <option value="">Every alert</option>
            {Object.entries(ALERT_KINDS).map(([value, k]) => <option key={value} value={value}>{k.label}</option>)}
          </select>
        </div>
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}
        <FleetGrid title="alerts sent" tableRef={tableRef} exportName="alerts-sent">
          <TableAgClient
            key={kind}
            ref={tableRef}
            apiList={gridList}
            onReady={load}
            rowHeight={ROW_HEIGHT_TWO_LINE}
            sortCol="sentAt"
            sortDesc
            emptyState={{
              Icon: IconNotification,
              title: 'No alerts sent',
              message: kind ? `No "${kindOf(kind).label.toLowerCase()}" alerts have been sent.` : 'No alerts sent yet. They appear here as the daily checks warn about expiring credentials and domains.'
            }}>
            <ColumnFormat field="sentAt" header="Sent" render={agoCell} sortable />
            <ColumnFormat field="alert" header="Alert" render={alertCell} stacked minWidth={200} flex={2} sortable />
            <ColumnFormat field="about" header="About" render={row => (row.data ? subjectLink(row.data) : '')} sortable />
            <ColumnFormat field="organization" header="Organization" render={organizationCell} sortable />
            <ColumnText field="recipient" header="Sent to" sortable />
          </TableAgClient>
        </FleetGrid>
        {more &&
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-sm sw-btn" disabled={loadingMore} onClick={loadOlder}>{loadingMore ? 'Loading…' : 'Load older alerts'}</button>
          </div>}
      </FleetPage>
    </BreadcrumbView>
  )
}

const agoCell = row => (row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')

const alertCell = row =>
  row.data
    ? <span><Pill tone={kindOf(row.data.kind).tone}>{row.value}</Pill>{alertDetail(row.data) && <span className="sw-cell-sub">{alertDetail(row.data)}</span>}</span>
    : ''

const organizationCell = row => row.value || <span className="sw-hint">EducationXR</span>
